import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { vehicleApi, reservationApi } from '../../lib/api';
import { useAuthStore } from '../../stores/auth';
import { useCart } from '../../hooks/useCart';
import DatePicker from '../../components/shared/DatePicker';
import toast from 'react-hot-toast';

export default function VehicleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cart, addVehicle, removeVehicle, setDates, setLocation, clearCart, hasVehicle } = useCart();

  const isMultiMode = searchParams.get('cart') === '1' && cart.vehicles.length > 1;
  const isInCart = id ? hasVehicle(id) : false;

  const [vehicle, setVehicle] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [pickupDate, setPickupDate] = useState(cart.pickupDate || '');
  const [returnDate, setReturnDate] = useState(cart.returnDate || '');
  const [pickupLocation, setPickupLocation] = useState(cart.pickupLocation || '');
  const [guestNotes, setGuestNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(isMultiMode);

  useEffect(() => {
    if (!id) return;
    vehicleApi.getById(id).then((res: any) => {
      setVehicle(res);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;
  }
  if (!vehicle) {
    return <div className="text-center py-20 text-gray-400">차량 정보를 찾을 수 없습니다</div>;
  }

  const formatPrice = (won: number) => new Intl.NumberFormat('ko-KR').format(won) + '원';

  const calculateRentalDays = () => {
    if (!pickupDate || !returnDate) return 0;
    const pickup = new Date(pickupDate);
    const returnD = new Date(returnDate);
    return Math.max(1, Math.ceil((returnD.getTime() - pickup.getTime()) / 86400000));
  };

  const rentalDays = calculateRentalDays();
  const dailyRate = vehicle.daily_rate;
  let effectiveRate = dailyRate;
  if (rentalDays >= 30 && vehicle.monthly_discount_pct) {
    effectiveRate = Math.round(dailyRate * (1 - vehicle.monthly_discount_pct / 100));
  } else if (rentalDays >= 7 && vehicle.weekly_discount_pct) {
    effectiveRate = Math.round(dailyRate * (1 - vehicle.weekly_discount_pct / 100));
  }
  const estimatedTotal = effectiveRate * rentalDays;

  const handleAddToCart = () => {
    if (!user) { navigate('/signin'); return; }
    if (!pickupDate || !returnDate) { toast.error('먼저 인도일과 반납일을 선택해주세요'); return; }
    if (!vehicle) return;

    // Save dates to cart
    setDates(pickupDate, returnDate);
    if (pickupLocation) setLocation(pickupLocation);

    // Add vehicle to cart
    addVehicle({
      id: vehicle.id,
      make: vehicle.make,
      model: vehicle.model,
      year: vehicle.year,
      daily_rate: vehicle.daily_rate,
      primary_photo: vehicle.photos?.[0]?.photo_url,
    });

    toast.success(`${vehicle.make} ${vehicle.model} 장바구니에 담았습니다`);
  };

  const handleMultiReserve = async () => {
    if (!user) { navigate('/signin'); return; }
    if (!pickupDate || !returnDate) { toast.error('날짜를 선택해주세요'); return; }
    setSubmitting(true);
    try {
      const res: any = await reservationApi.create({
        vehicle_ids: cart.vehicles.map(v => v.id),
        pickup_date: pickupDate,
        return_date: returnDate,
        pickup_location: pickupLocation || undefined,
        guest_notes: guestNotes || undefined,
      });
      toast.success(`${cart.vehicles.length}대 차량 예약이 요청되었습니다`);
      clearCart();
      navigate(`/reservations/${res.id}`);
    } catch (err: any) {
      toast.error(err.message || '예약 요청에 실패했습니다');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSingleReserve = async () => {
    if (!user) { navigate('/signin'); return; }
    if (!pickupDate || !returnDate) { toast.error('날짜를 선택해주세요'); return; }
    setSubmitting(true);
    try {
      const res: any = await reservationApi.create({
        vehicle_id: id,
        pickup_date: pickupDate,
        return_date: returnDate,
        pickup_location: pickupLocation || undefined,
        guest_notes: guestNotes || undefined,
      });
      toast.success('예약이 요청되었습니다');
      navigate(`/reservations/${res.id}`);
    } catch (err: any) {
      toast.error(err.message || '예약 요청에 실패했습니다');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <Link to="/" className="text-sm text-brand-600 hover:underline mb-4 inline-block transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 rounded">← 검색 결과로 돌아가기</Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Photos */}
        <div className="lg:col-span-2">
          <div className="aspect-[16/10] bg-gray-100 rounded-xl overflow-hidden mb-4">
            {vehicle.photos?.[0]?.photo_url ? (
              <img src={vehicle.photos[0].photo_url} alt={`${vehicle.make} ${vehicle.model}`} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300 text-6xl">🚗</div>
            )}
          </div>
          {vehicle.photos?.length > 1 && (
            <div className="flex gap-2 overflow-x-auto">
              {vehicle.photos.slice(1).map((p: any) => (
                <img key={p.id} src={p.photo_url} alt="" className="w-24 h-24 object-cover rounded-lg" />
              ))}
            </div>
          )}
        </div>

        {/* Info & Booking */}
        <div className="card p-6">
          <div className="mb-4">
            <span className={`badge text-xs mr-2 ${vehicle.category === 'prop_car' ? 'badge-blue' : 'badge-green'}`}>
              {vehicle.category === 'prop_car' ? '소품차' : '렌트카'}
            </span>
            <span className="badge badge-gray text-xs">{vehicle.vehicle_type}</span>
          </div>

          <h1 className="text-2xl font-bold mb-1">{vehicle.make} {vehicle.model}</h1>
          <p className="text-gray-500 mb-4">{vehicle.color || '색상 미기재'} · {vehicle.location_city} {vehicle.location_district || ''}</p>

          {/* Pricing — post-settlement model */}
          <div className="border-t border-b border-gray-100 py-4 my-4 space-y-3">
            <div>
              <p className="text-sm text-gray-400 mb-1">예상 대여료</p>
              <p className="text-xl font-bold text-slate-900">{formatPrice(effectiveRate || dailyRate)}<span className="text-sm font-normal text-gray-400"> / 일</span></p>
              {rentalDays > 0 && (
                <p className="text-xs text-gray-400 mt-1">{rentalDays}일 예상: {formatPrice(effectiveRate * rentalDays)}</p>
              )}
            </div>

            <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-800 space-y-1">
              <p className="font-medium">사후 정산 안내</p>
              <ul className="space-y-0.5 text-amber-700">
                <li>· 대여료 외 케어·운송·보험 등 부가 비용은 촬영 종료 후 확정</li>
                <li>· 반납 시 최종 금액 확정 → 카드 청구 또는 세금계산서 발행</li>
                <li>· 카드 정보는 예약 확정 시 등록 (승인만 보류, 반납 후 청구)</li>
              </ul>
            </div>
          </div>

          {/* Booking Form */}
          <div className="space-y-3">
            {/* Date lock notice when cart already has vehicles */}
            {cart.vehicles.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-700">
                장바구니에 {cart.vehicles.length}대가 담겨있습니다. 모든 차량에 동일한 날짜가 적용됩니다.
              </div>
            )}
            <div className={`grid grid-cols-2 gap-2 ${cart.vehicles.length > 0 ? 'opacity-60 pointer-events-none' : ''}`}>
              <DatePicker
                label="인도일"
                value={pickupDate}
                onChange={setPickupDate}
                minDate={new Date().toISOString().split('T')[0]}
                placeholder="인도 희망일"
              />
              <DatePicker
                label="반납일"
                value={returnDate}
                onChange={setReturnDate}
                minDate={pickupDate || new Date().toISOString().split('T')[0]}
                placeholder="반납 희망일"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">인도 장소</label>
              <input type="text" className="input-field text-sm" value={pickupLocation} onChange={e => setPickupLocation(e.target.value)} placeholder="서울시 강남구..." />
            </div>
            <div>
              <label className="text-xs text-gray-500">요청 사항</label>
              <textarea className="input-field text-sm" rows={2} value={guestNotes} onChange={e => setGuestNotes(e.target.value)} placeholder="촬영 목적, 특이사항 등" />
            </div>

            {/* Card input — shown after inquiry is submitted */}
            {false && (
              <div className="border border-gray-200 rounded-xl p-3 space-y-2">
                <p className="text-xs font-medium text-gray-500">카드 등록 (승인 보류)</p>
                <input type="text" className="input-field text-sm" placeholder="카드번호 0000-0000-0000-0000" />
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" className="input-field text-sm" placeholder="MM/YY" />
                  <input type="text" className="input-field text-sm" placeholder="CVC" />
                </div>
                <p className="text-[11px] text-gray-400">반납 후 최종 금액으로 청구됩니다. 지금은 승인만 보류됩니다.</p>
              </div>
            )}

            {/* Multi-vehicle confirmation section */}
            {showConfirm && cart.vehicles.length > 1 && (
              <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 space-y-3">
                <h4 className="text-sm font-semibold text-brand-800">
                  견적 문의하기 ({cart.vehicles.length}대)
                </h4>
                <p className="text-xs text-brand-600">
                  선택한 모든 차량에 동일한 날짜({pickupDate || '미정'} ~ {returnDate || '미정'})가 적용됩니다.
                </p>
                <div className="space-y-1.5">
                  {cart.vehicles.map(v => (
                    <div key={v.id} className="flex items-center justify-between bg-white rounded-lg p-2.5 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        {v.primary_photo && <img src={v.primary_photo} alt="" className="w-10 h-7 rounded object-cover shrink-0" />}
                        <span className="font-medium text-slate-900 truncate">{v.make} {v.model}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-gray-500">{new Intl.NumberFormat('ko-KR').format(v.daily_rate)}원/일</span>
                        <button onClick={() => removeVehicle(v.id)} className="text-gray-300 hover:text-red-500 text-xs transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 rounded-full">✕</button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-sm font-semibold pt-2 border-t border-brand-200">
                  <span>예상 대여료</span>
                  <span>{formatPrice(cart.vehicles.reduce((s, v) => s + v.daily_rate * rentalDays, 0))}</span>
                </div>
                <button onClick={handleMultiReserve} disabled={!pickupDate || !returnDate || submitting}
                  className="w-full bg-brand-600 hover:bg-brand-700 text-white font-semibold py-3 rounded-xl transition-colors text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                  {submitting ? '처리 중...' : `${cart.vehicles.length}대 예약 요청`}
                </button>
              </div>
            )}

            {/* Single vehicle: add to cart button */}
            {!isInCart && !showConfirm && (
              <button onClick={handleAddToCart} disabled={!pickupDate || !returnDate}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 rounded-xl transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-brand-500">
                {user ? '장바구니 담기' : '로그인 후 문의'}
              </button>
            )}

            {/* Already in cart */}
            {isInCart && !showConfirm && (
              <div className="space-y-2">
                <div className="bg-green-50 border border-green-200 text-green-700 text-sm font-medium rounded-xl py-3 text-center">
                  ✓ 장바구니에 담겼습니다
                </div>
                <button onClick={() => removeVehicle(id!)}
                  className="w-full text-sm text-gray-400 hover:text-red-500 py-1 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 rounded">
                  장바구니에서 빼기
                </button>
                {cart.vehicles.length > 1 && (
                  <button onClick={() => setShowConfirm(true)}
                    className="w-full bg-brand-600 hover:bg-brand-700 text-white font-semibold py-3 rounded-xl transition-colors text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                    견적 문의하기 ({cart.vehicles.length}대)
                  </button>
                )}
                {cart.vehicles.length === 1 && (
                  <button onClick={handleSingleReserve} disabled={!pickupDate || !returnDate || submitting}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 rounded-xl transition-colors text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                    {submitting ? '처리 중...' : '바로 예약 문의'}
                  </button>
                )}
              </div>
            )}

            <p className="text-xs text-center text-gray-400">
              문의 → 검토 → 견적 확정 → 카드 등록 → 촬영 → 반납 → 최종 정산
            </p>
          </div>

          {/* Vehicle Details */}
          <div className="mt-6 space-y-3 text-sm">
            <h3 className="font-semibold text-gray-900">차량 정보</h3>
            {vehicle.description && <p className="text-gray-600">{vehicle.description}</p>}
            {vehicle.features && <p className="text-gray-600">{vehicle.features}</p>}
            <div className="grid grid-cols-2 gap-1 text-gray-500">
              {vehicle.license_plate && <p>차량번호: {vehicle.license_plate}</p>}
              {vehicle.mileage && <p>주행거리: {vehicle.mileage.toLocaleString()}km</p>}
              <p>일반도로 주행: {vehicle.can_drive_public_road ? '가능' : '제한됨'}</p>
              <p>캐리어 탁송: {vehicle.carrier_available ? '가능' : '불가'}</p>
              {vehicle.deposit_required > 0 && <p>보증금: {formatPrice(vehicle.deposit_required)}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
